import {
  FC,
  useCallback,
  useState,
  useEffect,
  useMemo,
  Dispatch,
  SetStateAction,
} from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import BasicModal from "../BasicModal";
import { getDeliveryAddresses } from "api/creditco";
import { convertCardToPhysical, getUsersCardsList } from "api/cardProduct";
import { useFormik } from "formik";
import { WalletStore, CurrenciesStore, GlobalStore } from "stores";
import { SelectItemProps } from "components/Inputs/types";
import MainPurchasePlasticStep from "./MainPurchasePlasticStep";
import SuccessPurchasePlasticStep from "./SuccessPurchasePlasticStep";
import { EeaCountries } from "helpers/consts";
import { requiredField } from "helpers/errorMessages";
import { normolizeCurrenciesDecimals } from "helpers/funcs";
import {
  exceedAmount,
  unavailablePair,
  deliveryAddressRestriction,
} from "helpers/errorMessages";
import type {
  ModalStepsType,
  DeliveryAddress,
  AvailableTradeOptions,
  WalletsListInterface,
} from "helpers/types";
import type { ConvertCardToPhysicalReq } from "api/types/creditco";
import type { UsersCard } from "api/types/cardProduct";

interface InitialErrors {
  deliveryAddressId?: string;
  deliveryMethodId?: string;
}
export interface CostsInterface {
  cardCost: number;
  deliveryMethodCost: number;
}

interface PurchasePlasticCryptoCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardInfo: UsersCard | null;
  setCardInfo: Dispatch<SetStateAction<UsersCard | null>>;
  currencyVariants: AvailableTradeOptions[];
}

const PurchasePlasticCryptoCardModal: FC<
  PurchasePlasticCryptoCardModalProps
> = ({ isOpen, onClose, cardInfo, setCardInfo, currencyVariants }) => {
  const { t } = useTranslation();
  const { walletsList } = WalletStore;
  const { currencies, prices } = CurrenciesStore;
  const [selectedCurrency, setSelectedCurrency] =
    useState<AvailableTradeOptions | null>(null);

  const [step, setStep] = useState<ModalStepsType>("main");
  const [deliveryAddresses, setDeliveryAddresses] = useState<
    DeliveryAddress[] | null
  >(null);

  const {
    values,
    touched,
    errors,
    isSubmitting,
    handleSubmit,
    handleChange,
    handleBlur,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      deliveryAddressId: "",
      deliveryMethodId: "",
    },

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      const changePassData: ConvertCardToPhysicalReq = {
        deliveryAddressId: Number(values.deliveryAddressId),
        deliveryMethodId: Number(values.deliveryMethodId),
        currencyName: selectedCurrency!,
      };

      convertCardToPhysical(cardInfo!.card.id, changePassData)
        .then(() => {
          const newCardInfo = { ...cardInfo! };
          newCardInfo.card.cardType = "PHYSICAL";
          setCardInfo(newCardInfo);
          getUsersCardsList();
          setStep("success");
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            GlobalStore.setError(err.response.data.message);
          }
        })
        .finally(() => setSubmitting(false));
    },

    validate: (values) => {
      const errors: InitialErrors = {};
      if (!values.deliveryAddressId) {
        errors.deliveryAddressId = requiredField;
      }
      if (!values.deliveryMethodId) {
        errors.deliveryMethodId = requiredField;
      }
      if (values.deliveryAddressId && deliveryAddresses) {
        const currentCountry = deliveryAddresses?.find(
          ({ id }) => String(id) === values.deliveryAddressId
        )?.country;

        if (currentCountry && !EeaCountries.includes(currentCountry)) {
          errors.deliveryAddressId = deliveryAddressRestriction;
        }
      }

      return errors;
    },
  });

  const mainCostValue = useMemo<number>(() => {
    if (!prices || !cardInfo) {
      return 0;
    }
    if (
      cardInfo.cardProductTemplate.cardMaterial?.currencyName ===
      selectedCurrency
    ) {
      return 1;
    }
    let mainCost: string | number =
      prices[
        `${selectedCurrency}/${
          cardInfo.cardProductTemplate.cardMaterial?.currencyName || "EUR"
        }`
      ]?.close;

    if (!mainCost) {
      const usdCoinCost = prices[`${selectedCurrency}/USD`]?.close;
      const usdtValue = prices["USDT/USD"]?.close;

      if (!usdtValue || !usdCoinCost) {
        return 0;
      }

      mainCost = Number(usdCoinCost) / Number(usdtValue);
    }

    return Number(mainCost) || 0;
  }, [prices, cardInfo, selectedCurrency]);

  const costInfos = useMemo<CostsInterface>(() => {
    if (!cardInfo || !values.deliveryMethodId || !mainCostValue) {
      return {
        cardCost: 0,
        deliveryMethodCost: 0,
      };
    }
    const deliveryInfo = cardInfo.cardProductTemplate.cardDeliveryMethods.find(
      ({ id }) => id === Number(values.deliveryMethodId)
    );
    const cardCostInfo = cardInfo.cardProductTemplate.cardMaterial;

    if (!cardCostInfo) {
      return {
        cardCost: 0,
        deliveryMethodCost: 0,
      };
    }
    return {
      cardCost: Number(cardCostInfo.cost) / Number(mainCostValue),
      deliveryMethodCost: deliveryInfo
        ? Number(deliveryInfo.cost) / Number(mainCostValue)
        : 0,
    };
  }, [cardInfo, values.deliveryMethodId, mainCostValue]);

  const addressOptions = useMemo<Array<SelectItemProps>>(() => {
    if (!deliveryAddresses) {
      return [];
    }
    return deliveryAddresses
      .filter(({ country }) => EeaCountries.includes(country))
      .map(({ city, address, postCode, id }) => ({
        value: id,
        label: `${city}, ${address}, ${postCode}`,
      }));
  }, [deliveryAddresses]);

  const deliveryMethodsOptions = useMemo<Array<SelectItemProps>>(() => {
    if (!cardInfo?.cardProductTemplate.cardDeliveryMethods) {
      return [];
    }
    return cardInfo.cardProductTemplate.cardDeliveryMethods.map(
      ({ type, id, cost }) => ({
        value: id,
        label: `${t(type)} (${normolizeCurrenciesDecimals(
          Number(cost) / mainCostValue,
          selectedCurrency || "USDT",
          currencies
        )} ${selectedCurrency})`,
      })
    );
  }, [cardInfo, t, mainCostValue, selectedCurrency, currencies]);

  useEffect(() => {
    if (!values.deliveryAddressId && addressOptions.length > 0) {
      setFieldValue("deliveryAddressId", addressOptions[0].value);
    }
    if (!values.deliveryMethodId && deliveryMethodsOptions.length > 0) {
      setFieldValue("deliveryMethodId", deliveryMethodsOptions[0].value);
    }
  }, [
    addressOptions,
    values.deliveryAddressId,
    values.deliveryMethodId,
    deliveryMethodsOptions,
    setFieldValue,
  ]);

  const resetState = useCallback(() => {
    resetForm();
    setStep("main");
  }, [resetForm]);

  const setNewAddress = useCallback(
    (id: number) => {
      setFieldValue("deliveryAddressId", id);
    },
    [setFieldValue]
  );

  const setNewDeliveryAddresses = useCallback(
    (address: DeliveryAddress) =>
      setDeliveryAddresses(
        deliveryAddresses ? [...deliveryAddresses, address] : [address]
      ),
    [deliveryAddresses]
  );

  useEffect(() => {
    if (!cardInfo) {
      return;
    }
    if (!selectedCurrency && cardInfo.cardProductTemplate.isRwa) {
      setSelectedCurrency(cardInfo.cardProductTemplate.currencyName);
      return;
    }
    if (!selectedCurrency && currencyVariants.length > 0) {
      if (cardInfo.cardProductTemplate.currencyName === currencyVariants[0]) {
        setSelectedCurrency(currencyVariants[1]);
        return;
      }
      if (!prices) {
        setSelectedCurrency(currencyVariants[0]);
        return;
      }
      const mainUsersCoin = [...walletsList!]
        .filter(({ currencyName }) => currencyName !== "STBU")
        .reduce((acc: WalletsListInterface | null, coin) => {
          const coinValue =
            Number(coin.balance) *
              Number(prices![`${coin.currencyName}/USD`]?.close) || 0;

          const currentCoinValue =
            Number(acc?.balance) *
              Number(prices![`${acc?.currencyName}/USD`]?.close) || 0;

          if (coinValue > currentCoinValue) {
            acc = coin;
          }
          return acc;
        }, null);

      if (mainUsersCoin) {
        setSelectedCurrency(mainUsersCoin.currencyName);
        return;
      }
      setSelectedCurrency(currencyVariants[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency, currencyVariants, cardInfo, walletsList]);

  const fullCurrencyBalance = useMemo<number>(() => {
    const balance = walletsList?.find(
      ({ currencyName }) => selectedCurrency === currencyName
    )?.balance;

    return !selectedCurrency || !balance ? 0 : Number(balance);
  }, [walletsList, selectedCurrency]);

  const hasError = useMemo<string | null>(() => {
    if (!costInfos || !cardInfo || !prices || !selectedCurrency) {
      return null;
    }

    if (mainCostValue === 0) {
      return unavailablePair;
    }

    const value =
      Number(costInfos.cardCost) + Number(costInfos.deliveryMethodCost);

    if (!value) {
      return null;
    }

    if (value / Number(mainCostValue) > fullCurrencyBalance) {
      return exceedAmount;
    }
    return null;
  }, [
    selectedCurrency,
    mainCostValue,
    costInfos,
    cardInfo,
    fullCurrencyBalance,
    prices,
  ]);

  useEffect(() => {
    if (isOpen && !deliveryAddresses) {
      getDeliveryAddresses().then(({ data }) =>
        setDeliveryAddresses(data.items)
      );
    }
  }, [isOpen, deliveryAddresses]);

  return (
    <BasicModal
      isOpen={isOpen}
      onExited={resetState}
      onClose={onClose}
      title={t("PURCHASE_PLASTIC_CARD")}
      showLoader={isSubmitting}
    >
      {step === "main" && (
        <MainPurchasePlasticStep
          values={values}
          touched={touched}
          errors={errors}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          handleChange={handleChange}
          handleBlur={handleBlur}
          onClose={onClose}
          addressOptions={addressOptions}
          deliveryMethodsOptions={deliveryMethodsOptions}
          cardInfo={cardInfo}
          setNewDeliveryAddresses={setNewDeliveryAddresses}
          setNewAddress={setNewAddress}
          currencies={currencies}
          costInfos={costInfos}
          selectedCurrency={selectedCurrency}
          walletsList={walletsList}
          setSelectedCurrency={setSelectedCurrency}
          hasError={hasError}
          cost={mainCostValue}
          currencyVariants={currencyVariants}
        />
      )}

      {step === "success" && <SuccessPurchasePlasticStep onClose={onClose} />}
    </BasicModal>
  );
};

export default observer(PurchasePlasticCryptoCardModal);
